import { api } from '../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../shared/api/genexus-query';
import type { CrudApi } from '../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../shared/components/DataTable';
import type {
  PurchaseRequestDto,
  PurchaseRequestInput,
  PurchaseRequestListItem,
  RcAjusteManualInput,
  RcComplementarInput,
  RcEmitirNfInput,
  RcEncerrarCancelarInput,
  RcMovimentoEstornoInput,
  RcMovimentoItem,
  RcProjetoVinculado,
} from './types';

const BASE = '/v1/purchase-requests';

/** Filtros da lista de RC (card [jJfGSfbJ]). `status` = ATV/ENC/CAN. */
export interface PurchaseRequestListFiltros {
  status?: PurchaseRequestListItem['status'];
}

/**
 * List de RC com filtros opcionais. O `ApiPurchaseRequestList` já aceita
 * `status` (filtra `RequisicaoCompraStatus`); `status=CAN` traz as canceladas
 * (que ficam ocultas por padrão).
 */
export async function listPurchaseRequests(
  query: DataTableQuery,
  filtros?: PurchaseRequestListFiltros,
): Promise<ListEnvelope<PurchaseRequestListItem>> {
  const { data } = await api.get<{
    data: PurchaseRequestListItem[];
    meta: { total: number; pagina?: number; porPagina?: number };
  }>(BASE, { params: toGenexusParams(query, { status: filtros?.status || undefined }) });
  return normalizeListEnvelope(data);
}

export const purchaseRequestsApi: CrudApi<
  PurchaseRequestListItem,
  PurchaseRequestDto,
  PurchaseRequestInput
> = {
  async list(query: DataTableQuery): Promise<ListEnvelope<PurchaseRequestListItem>> {
    return listPurchaseRequests(query);
  },

  async get(id: number): Promise<PurchaseRequestDto> {
    const { data } = await api.get<{ data: PurchaseRequestDto }>(`${BASE}/${id}`);
    return data.data;
  },

  async create(input: PurchaseRequestInput): Promise<PurchaseRequestDto> {
    const { data } = await api.post<{ data: PurchaseRequestDto }>(BASE, input);
    return data.data;
  },

  async update(id: number, input: PurchaseRequestInput): Promise<PurchaseRequestDto> {
    // valorInicial é ignorado no servidor — enviado por consistência de shape.
    const { data } = await api.put<{ data: PurchaseRequestDto }>(`${BASE}/${id}`, input);
    return data.data;
  },

  async remove(id: number): Promise<void> {
    // DELETE = transição para Status=CAN. Spec § 6.
    await api.delete(`${BASE}/${id}`);
  },
};

/**
 * Extrato de movimentos da RC (read-only, ordem cronológica). Card [89dB3aaI].
 * `porPagina` alto — extrato de uma RC é conjunto pequeno, front não pagina.
 */
export async function listRcMovimentos(purchaseRequestId: number): Promise<RcMovimentoItem[]> {
  const { data } = await api.get<{ data: RcMovimentoItem[] }>(
    `${BASE}/${purchaseRequestId}/movements`,
    { params: { pagina: 1, porPagina: 200 } },
  );
  return data.data;
}

/**
 * Estorna um lançamento (só MAN/COM não estornados — validado no server).
 * Backend embrulha `doRequisicaoCompraLancamentoEstorno` + a notificação.
 */
export async function estornarRcMovimento(
  purchaseRequestId: number,
  movimentoId: string,
  input: RcMovimentoEstornoInput,
): Promise<void> {
  await api.post(`${BASE}/${purchaseRequestId}/movements/${movimentoId}/estornar`, input);
}

/** Complementar — cria movimento de crédito (COM/+). Card [zn9Ds1NQ]. */
export async function complementarRcMovimento(
  purchaseRequestId: number,
  input: RcComplementarInput,
): Promise<void> {
  await api.post(`${BASE}/${purchaseRequestId}/movements/complementar`, input);
}

/** Ajuste Manual — cria movimento MAN com `tipo` (+/-). Card [zn9Ds1NQ]. */
export async function ajusteManualRcMovimento(
  purchaseRequestId: number,
  input: RcAjusteManualInput,
): Promise<void> {
  await api.post(`${BASE}/${purchaseRequestId}/movements/ajuste-manual`, input);
}

/** Encerrar RC — movimento ENC débito=saldo, status→ENC. Card [vuaj4L1x]. */
export async function encerrarRc(
  purchaseRequestId: number,
  input: RcEncerrarCancelarInput,
): Promise<void> {
  await api.post(`${BASE}/${purchaseRequestId}/encerrar`, input);
}

/** Cancelar RC — movimento CAN débito=saldo, status→CAN. Card [vuaj4L1x].
 * Substitui o DELETE restrito (que só cancelava RC não usada). */
export async function cancelarRc(
  purchaseRequestId: number,
  input: RcEncerrarCancelarInput,
): Promise<void> {
  await api.post(`${BASE}/${purchaseRequestId}/cancelar`, input);
}

/** Emitir NF — gera a Nota-Mãe da RC (valor = saldo). Card [spjIqsFB]. */
export async function emitirNotaFiscalRc(
  purchaseRequestId: number,
  input: RcEmitirNfInput,
): Promise<void> {
  await api.post(`${BASE}/${purchaseRequestId}/emitir-nota-fiscal`, input);
}

/**
 * RCs de um cliente — usado como "disponíveis" no vínculo reverso projeto→RC
 * (card [pusd8Icv]). `ApiPurchaseRequestList` já aceita `clienteId`.
 */
export async function listRcsByCliente(clienteId: number): Promise<PurchaseRequestListItem[]> {
  const { data } = await api.get<{ data: PurchaseRequestListItem[] }>(BASE, {
    params: toGenexusParams({ page: 1, pageSize: 200 }, { clienteId }),
  });
  return data.data;
}

/** Projetos vinculados à RC. Card [uJoKy8Qk]. */
export async function listRcProjetos(purchaseRequestId: number): Promise<RcProjetoVinculado[]> {
  const { data } = await api.get<{ data: RcProjetoVinculado[] }>(
    `${BASE}/${purchaseRequestId}/projetos`,
  );
  return data.data;
}

/** Vincula um projeto do cliente da RC. */
export async function linkRcProjeto(
  purchaseRequestId: number,
  clienteProjetoCodigo: number,
): Promise<void> {
  await api.post(`${BASE}/${purchaseRequestId}/projetos`, { clienteProjetoCodigo });
}

/** Desvincula um projeto. */
export async function unlinkRcProjeto(
  purchaseRequestId: number,
  clienteProjetoCodigo: number,
): Promise<void> {
  await api.delete(`${BASE}/${purchaseRequestId}/projetos/${clienteProjetoCodigo}`);
}
